import React, { useState } from 'react';

/**
 * AuditReport — detailed pipeline audit shown after completion.
 * Rendered as an expandable panel below the pipeline graph.
 */

function getSecFix(finding) {
  const f = finding.toLowerCase();
  if (f.includes('md5') || f.includes('sha1'))
    return 'Replace with hashlib.pbkdf2_hmac or bcrypt for passwords, hashlib.sha256 for checksums.';
  if (f.includes('eval(') || f.includes('exec('))
    return 'Remove eval/exec entirely. Use explicit logic or ast.literal_eval for safe parsing.';
  if (f.includes('shell=true'))
    return 'Pass command as a list: subprocess.run(["cmd", "arg"]) — never shell=True with user input.';
  if (f.includes('pickle'))
    return 'Use json.loads/json.dumps instead. Pickle executes arbitrary code on deserialization.';
  if (f.includes('non-cryptographic random') || f.includes('random.'))
    return 'Use secrets.token_hex() or secrets.token_urlsafe() for security-sensitive values.';
  if (f.includes('debug mode'))
    return 'Set DEBUG=False in production via environment variable.';
  if (f.includes('ssl') || f.includes('verify=false'))
    return 'Never disable SSL verification. Ensure valid certificates are in place.';
  if (f.includes('hardcoded password') || f.includes('hardcoded secret'))
    return 'Move to environment variables: os.environ.get("SECRET_KEY"). Use a secrets manager in production.';
  if (f.includes('print('))
    return 'Replace print() with logging.getLogger(__name__).info(). Configure log level via environment.';
  if (f.includes('broad exception') || f.includes('except:'))
    return 'Catch specific exceptions: except ValueError as e. Log the error before handling.';
  if (f.includes('todo') || f.includes('fixme') || f.includes('hack'))
    return 'Resolve before deploying to production. Create a tracked issue if deferring.';
  return 'Review this finding and apply the principle of least privilege / secure defaults.';
}

const DECISION_COLOR = {
  Refactor: { bg: '#FFF8C5', color: '#9A6700', border: '#9A670044' },
  New:      { bg: '#DAFBE1', color: '#1A7F37', border: '#1A7F3744' },
  Retire:   { bg: '#FFEBE9', color: '#CF222E', border: '#CF222E44' },
};

const QUALITY_COLOR = {
  pass:             '#1A7F37',
  warn_and_proceed: '#9A6700',
  retry:            '#0969DA',
  block:            '#CF222E',
};

function StatBox({ label, value, sub, color }) {
  return (
    <div style={{
      background: '#fff', border: '1px solid #D0D7DE', borderRadius: 8,
      padding: '12px 16px', flex: 1, minWidth: 100,
      borderTop: `3px solid ${color || '#0969DA'}`,
    }}>
      <div style={{ fontSize: 22, fontWeight: 800, color: color || '#1A1F2E' }}>{value}</div>
      <div style={{ fontSize: 11, fontWeight: 700, color: '#57606A', marginTop: 2 }}>{label}</div>
      {sub && <div style={{ fontSize: 10, color: '#8B949E', marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

function AppRow({ app }) {
  const [open, setOpen] = useState(false);
  const dc = DECISION_COLOR[app.decision] || { bg: '#F6F8FA', color: '#57606A', border: '#D0D7DE' };
  const covColor = app.coverage >= 70 ? '#1A7F37' : app.coverage >= 40 ? '#9A6700' : '#CF222E';

  return (
    <div style={{ borderBottom: '1px solid #D0D7DE' }}>
      {/* Summary row */}
      <div
        onClick={() => setOpen(o => !o)}
        style={{
          display: 'grid',
          gridTemplateColumns: '28px 1fr 90px 80px 80px 80px 80px 28px',
          gap: 0, padding: '10px 16px', cursor: 'pointer',
          background: open ? '#F6F8FA' : '#fff',
          alignItems: 'center',
        }}
      >
        <span style={{ fontSize: 16 }}>
          {app.decision === 'Retire' ? '🗑️' : app.decision === 'New' ? '🆕' : '🔧'}
        </span>

        <div>
          <div style={{ fontWeight: 700, fontSize: 12, color: '#1A1F2E' }}>{app.name}</div>
          <div style={{ fontSize: 10, color: '#57606A' }}>
            {app.language} · {app.framework} · {app.loc?.toLocaleString()} LOC
          </div>
        </div>

        <span style={{
          fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 8,
          background: dc.bg, color: dc.color, border: `1px solid ${dc.border}`,
          textAlign: 'center',
        }}>
          {app.decision}
          {app.auto_decided && <span style={{ marginLeft: 4, opacity: 0.7 }}>🤖</span>}
        </span>

        {/* Coverage */}
        <div style={{ textAlign: 'center' }}>
          {app.code_generated ? (
            <span style={{ fontSize: 12, fontWeight: 700, color: covColor }}>
              {app.coverage?.toFixed(0)}%
            </span>
          ) : <span style={{ color: '#D0D7DE', fontSize: 11 }}>—</span>}
          <div style={{ fontSize: 9, color: '#8B949E' }}>coverage</div>
        </div>

        {/* Tests */}
        <div style={{ textAlign: 'center' }}>
          {app.code_generated ? (
            <span style={{ fontSize: 12, fontWeight: 700, color: app.test_failed > 0 ? '#9A6700' : '#1A7F37' }}>
              {app.test_passed}/{app.test_passed + app.test_failed}
            </span>
          ) : <span style={{ color: '#D0D7DE', fontSize: 11 }}>—</span>}
          <div style={{ fontSize: 9, color: '#8B949E' }}>tests</div>
        </div>

        {/* Security */}
        <div style={{ textAlign: 'center' }}>
          {app.code_generated ? (
            <span style={{ fontSize: 12, fontWeight: 700, color: app.security_high > 0 ? '#CF222E' : '#1A7F37' }}>
              {app.security_high}H {app.security_medium}M
            </span>
          ) : <span style={{ color: '#D0D7DE', fontSize: 11 }}>—</span>}
          <div style={{ fontSize: 9, color: '#8B949E' }}>security</div>
        </div>

        {/* Retries */}
        <div style={{ textAlign: 'center' }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: app.retry_count > 0 ? '#0969DA' : '#8B949E' }}>
            {app.retry_count > 0 ? `×${app.retry_count}` : '—'}
          </span>
          <div style={{ fontSize: 9, color: '#8B949E' }}>retries</div>
        </div>

        <span style={{ color: '#8B949E', fontSize: 12, transform: open ? 'rotate(90deg)' : 'none', transition: 'transform 0.2s', textAlign: 'center' }}>›</span>
      </div>

      {/* Expanded detail */}
      {open && (
        <div style={{ padding: '12px 16px 16px 44px', background: '#F6F8FA', display: 'flex', flexDirection: 'column', gap: 10 }}>
          {/* AI reasoning */}
          {app.llm_reasoning && (
            <div>
              <div style={{ fontSize: 10, fontWeight: 700, color: '#57606A', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>
                AI Assessment (risk score {app.llm_score?.toFixed(1)}/10)
              </div>
              <div style={{ fontSize: 12, color: '#1A1F2E', lineHeight: 1.6, background: '#fff', border: '1px solid #D0D7DE', borderRadius: 6, padding: '8px 12px' }}>
                {app.llm_reasoning}
              </div>
            </div>
          )}

          {/* Security findings */}
          {app.security_findings?.length > 0 && (
            <div>
              <div style={{ fontSize: 10, fontWeight: 700, color: '#57606A', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 }}>
                Security Findings &amp; How to Fix
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {app.security_findings.map((f, i) => {
                  const sevMatch = f.match(/^\[?(HIGH|MEDIUM|LOW)\]?\s*/i);
                  const sev = sevMatch ? sevMatch[1].toUpperCase() : 'LOW';
                  const msg = f.replace(/^\[?(HIGH|MEDIUM|LOW)\]?\s*/i, '').trim();
                  const sevColor = sev === 'HIGH' ? '#CF222E' : sev === 'MEDIUM' ? '#9A6700' : '#1A7F37';
                  const sevBg    = sev === 'HIGH' ? '#FFEBE9' : sev === 'MEDIUM' ? '#FFF8C5' : '#DAFBE1';
                  const fix = getSecFix(f);
                  return (
                    <div key={i} style={{ background: '#fff', border: '1px solid #D0D7DE', borderRadius: 6, overflow: 'hidden' }}>
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '7px 10px' }}>
                        <span style={{ fontSize: 9, fontWeight: 700, padding: '2px 6px', borderRadius: 4,
                          background: sevBg, color: sevColor, flexShrink: 0, marginTop: 1 }}>{sev}</span>
                        <span style={{ fontSize: 12, color: '#1A1F2E', lineHeight: 1.5 }}>{msg}</span>
                      </div>
                      <div style={{ padding: '0 10px 8px 10px', fontSize: 11, color: '#57606A', lineHeight: 1.6,
                        borderTop: '1px solid #EFF2F5', paddingTop: 6 }}>
                        <span style={{ fontWeight: 700, color: '#1A7F37' }}>Fix: </span>{fix}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Quality gate */}
          {app.quality_decision && (
            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
              <span style={{
                fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 6,
                background: (QUALITY_COLOR[app.quality_decision] || '#57606A') + '18',
                color: QUALITY_COLOR[app.quality_decision] || '#57606A',
                flexShrink: 0,
              }}>
                {app.quality_decision === 'pass' ? 'PASSED'
                  : app.quality_decision === 'warn_and_proceed' ? 'PASSED WITH WARNINGS'
                  : app.quality_decision === 'retry' ? 'RETRIED'
                  : app.quality_decision === 'block' ? 'BLOCKED'
                  : app.quality_decision?.toUpperCase()}
              </span>
              <div style={{ fontSize: 12, color: '#57606A', lineHeight: 1.5 }}>{app.quality_reasoning}</div>
            </div>
          )}

          {/* Retry history */}
          {app.retry_history?.length > 0 && (
            <div>
              <div style={{ fontSize: 10, fontWeight: 700, color: '#57606A', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>
                Retry History
              </div>
              {app.retry_history.map((h, i) => (
                <div key={i} style={{ fontSize: 11, color: '#57606A', fontFamily: 'monospace', background: '#fff', border: '1px solid #D0D7DE', borderRadius: 4, padding: '4px 8px', marginBottom: 3 }}>
                  {h}
                </div>
              ))}
            </div>
          )}

          {/* Migration complexity */}
          {app.migration_complexity && (
            <div style={{ fontSize: 11, color: '#57606A' }}>
              Migration complexity: <strong style={{ color: '#1A1F2E' }}>{app.migration_complexity}</strong>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function AuditReport({ report }) {
  const [open, setOpen] = useState(true);
  if (!report) return null;

  const {
    deployment_status, pipeline_confidence, apps_total,
    apps_modernized, apps_retired, auto_decided_count, hitl_count,
    total_retries, test_passed, test_failed, avg_coverage,
    security_high, iac_generated, terraform_lines, cdk_lines,
    app_summaries = [], supervisor_decisions = [], messages = [],
  } = report;

  return (
    <div style={{
      background: '#fff', border: '1px solid #D0D7DE', borderRadius: 10,
      overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
    }}>
      {/* Header */}
      <div
        onClick={() => setOpen(o => !o)}
        style={{
          padding: '12px 18px', cursor: 'pointer',
          background: '#DAFBE1', borderBottom: open ? '1px solid #D0D7DE' : 'none',
          display: 'flex', alignItems: 'center', gap: 10,
        }}
      >
        <span style={{ fontSize: 18 }}>📋</span>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 800, fontSize: 14, color: '#1A7F37' }}>Modernization Summary</div>
          <div style={{ fontSize: 11, color: '#57606A', marginTop: 1 }}>{deployment_status}</div>
        </div>
        <span style={{ fontSize: 11, color: '#57606A', marginRight: 8 }}>
          Confidence: <strong style={{ color: '#1A7F37' }}>{(pipeline_confidence * 100).toFixed(0)}%</strong>
        </span>
        <span style={{ color: '#57606A', fontSize: 14, transform: open ? 'rotate(90deg)' : 'none', transition: 'transform 0.2s' }}>›</span>
      </div>

      {open && (
        <>
          {/* Stats row */}
          <div style={{ display: 'flex', gap: 10, padding: '14px 18px', flexWrap: 'wrap', background: '#F6F8FA', borderBottom: '1px solid #D0D7DE' }}>
            <StatBox label="Apps Scanned"    value={apps_total}        color="#0969DA" />
            <StatBox label="Modernized"      value={apps_modernized}   color="#1A7F37" />
            <StatBox label="Retired"         value={apps_retired}      color="#CF222E" />
            <StatBox label="AI Decided"      value={auto_decided_count} sub="automatically" color="#6639BA" />
            <StatBox label="Human Reviews"   value={hitl_count}        color="#9A6700" />
            <StatBox label="Tests Passed"    value={test_passed}       sub={`${avg_coverage?.toFixed(0)}% avg coverage`} color="#1A7F37" />
            <StatBox label="Tests Failed"    value={test_failed}       color={test_failed > 0 ? '#CF222E' : '#1A7F37'} />
            <StatBox label="Security Issues" value={security_high}     color={security_high > 0 ? '#CF222E' : '#1A7F37'} />
            <StatBox label="Auto-Retries"    value={total_retries}     color={total_retries > 0 ? '#0969DA' : '#8B949E'} />
            {iac_generated && <StatBox label="Infrastructure Lines" value={terraform_lines + cdk_lines} sub="Terraform + CDK" color="#0E7490" />}
          </div>

          {/* Per-app table */}
          <div>
            {/* Table header */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: '28px 1fr 90px 80px 80px 80px 80px 28px',
              gap: 0, padding: '7px 16px',
              background: '#F6F8FA', borderBottom: '1px solid #D0D7DE',
              fontSize: 10, fontWeight: 700, color: '#57606A', textTransform: 'uppercase', letterSpacing: '0.5px',
            }}>
              <div />
              <div>Application</div>
              <div>Decision</div>
              <div style={{ textAlign: 'center' }}>Coverage</div>
              <div style={{ textAlign: 'center' }}>Tests</div>
              <div style={{ textAlign: 'center' }}>Security</div>
              <div style={{ textAlign: 'center' }}>Retries</div>
              <div />
            </div>
            {app_summaries.map(app => (
              <AppRow key={app.name} app={app} />
            ))}
          </div>

          {/* Supervisor decisions */}
          {supervisor_decisions.length > 0 && (
            <div style={{ padding: '14px 18px', borderTop: '1px solid #D0D7DE' }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#57606A', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 }}>
                🤖 AI Routing Decisions
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {supervisor_decisions.map((d, i) => (
                  <div key={i} style={{ display: 'flex', gap: 8, fontSize: 11, alignItems: 'flex-start' }}>
                    <span style={{
                      fontSize: 9, fontWeight: 700, padding: '2px 6px', borderRadius: 4, flexShrink: 0,
                      background: d.action === 'auto_retire' ? '#FFEBE9' : d.action === 'auto_new' ? '#DAFBE1' : '#EFF2F5',
                      color: d.action === 'auto_retire' ? '#CF222E' : d.action === 'auto_new' ? '#1A7F37' : '#57606A',
                    }}>
                      {d.action === 'auto_retire' ? 'AI RETIRED'
                        : d.action === 'auto_new' ? 'AI REBUILD'
                        : d.action === 'escalate_hitl' ? 'SENT FOR REVIEW'
                        : d.action?.replace(/_/g, ' ').toUpperCase()}
                    </span>
                    <span style={{ color: '#57606A' }}>
                      <strong style={{ color: '#1A1F2E' }}>{d.app_name}</strong> — {d.reasoning}
                      <span style={{ color: '#8B949E', marginLeft: 6 }}>({(d.confidence * 100).toFixed(0)}% confidence)</span>
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Audit trail messages */}
          <div style={{ padding: '14px 18px', borderTop: '1px solid #D0D7DE' }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#57606A', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 }}>
              📝 Activity Log
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {messages.map((m, i) => (
                <div key={i} style={{ display: 'flex', gap: 8, fontSize: 11 }}>
                  <span style={{ color: '#8B949E', fontFamily: 'monospace', flexShrink: 0 }}>{String(i + 1).padStart(2, '0')}.</span>
                  <span style={{ color: '#57606A', fontFamily: 'monospace' }}>{m}</span>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
